import { isEmpty, isEqual, isNull } from "lodash";
import { areArraysEqual } from "..";

/* Zone Requirements Functions */
// Calculate the minimum area for a given number of occupants
export const calculateMinZoneArea = (occupants) => 4.5 * occupants;

// Calculate the minimum perimeter for a given area
export const calculateMinZonePerimeter = (floorArea) => 4 * Math.sqrt(floorArea);

export const checkRequirements = (requirements) => {
    return Object.values(requirements).every((requirement) => requirement);
};

export const getZoneStatusText = (status) => {
    switch (status) {
        case "results":
            return "Results available";
        case "noResults":
            return "No results available";
        case "changes":
            return "Changes to inputs have been made that may no longer align with the displayed calculation results. Please re-run the analysis to ensure the results are accurate.";
        case "error":
            return "This zone does not meet all of the requirements to serve as a cooling zone in the dwelling unit.";
        case "warning":
            return "This zone has warnings that could affect results. Please review the warnings and make the necessary adjustments.";
        default:
            return "";
    }
};

export const getCoolingZoneWarningText = (warning, value) => {
    switch (warning) {
        case "missingInteriorDoor":
            return "At least one interior wall in each cooling zone should have a door.";
        case "notFullyEnclosed":
            return "Double check that all interior walls which enclose the zone are defined and selected.";
        case "minWallPerimeter":
            return `Minimum wall perimeter suggested: ${value}`;
        case "missingComponent":
            return "This component may have been moved or deleted from the model.";
        default:
            return "";
    }
};

export const getCoolingZoneErrorText = (error, value) => {
    switch (error) {
        case "minAreaRequired":
            return `Must have an area of at least ${value}.`;
        case "minAreaRemaining":
            return `Add rooms to fill the remaining ${value}.`;
        case "requirementsNotMet":
            return "The cooling zone does not meet all of the required specifications.";
        case "requirementsNotMet2":
            return "Does not meet all of the required specifications.";

        default:
            return "";
    }
};

const ZONE_WARNING_KEYS = ["missingInteriorDoor", "notFullyEnclosed", "minWallPerimeter", "missingComponent"];
const ZONE_ERROR_KEYS = ["minAreaRequired", "minAreaRemaining", "requirementsNotMet", "requirementsNotMet2"];

export const getZoneFlagText = (key, value) => {
    if (ZONE_ERROR_KEYS.includes(key)) {
        return `Error: ${getCoolingZoneErrorText(key, value)}`;
    }

    if (ZONE_WARNING_KEYS.includes(key)) {
        return `Warning: ${getCoolingZoneWarningText(key, value)}`;
    }

    return key;
};

export const getZoneWarnings = (zone) => {
    const { warnings: { missingInteriorDoor = false, notFullyEnclosed = false } = {} } = zone || {};

    const messages = [];

    if (missingInteriorDoor) {
        messages.push("missingInteriorDoor");
    }

    if (notFullyEnclosed) {
        messages.push("notFullyEnclosed");
    }

    return messages;
};

export const getZoneErrors = (zone) => {
    const { requirements } = zone;

    if (!requirements || isEmpty(requirements)) {
        return [];
    }

    const messages = [];

    if (requirements.hasMinArea === false) {
        messages.push("minAreaRequired");
    }

    if (!checkRequirements(requirements)) {
        messages.push("requirementsNotMet2");
    }

    return messages;
};

export const getFieldText = (fieldName, textType) => {
    const texts = {
        indoorDesignTemp: {
            label: "Cooling Zone Indoor Design Temperature",
        },
        outdoorDesignTemp: {
            label: "Outdoor Design Temperature",
            lockNotice:
                "By default the outdoor design temperature is taken from the weather data. Note that changes to this field will invalidate some of your pre-existing calculations.",
        },
        occupants: {
            label: "Number of Occupants",
            lockNotice:
                "By default the number of occupants is the number of bedrooms plus one. Note that changes to this field will invalidate some of your pre-existing calculations.",
        },
        isFullyEnclosed: {
            label: "Is equipped with doors or other closures to separate it from the outdoors and other rooms or spaces within the dwelling unit.",
            info_false: "Select more rooms or interior walls to ensure the zone is fully enclosed.",
        },
        hasMinArea: {
            label: "Minimum Area of 48 ft² (4.5 m²) for each occupant",
            info_false: "Select more rooms to meet the minimum area requirement.",
        },
        hasWallOutlet: {
            label: "At least 1 standard electrical wall outlet",
        },
        hasOtherElectrical: {
            label: "Has all of the other electrical services required by the BCBC and the Canadian Electrical Code",
        },
        hasVentilation: {
            label: "Is equipped with provision for ventilation by",
        },
        homeHasCentralAirSystem: {
            label: "Is there a central air handling system in the home?",
            info_true: "A central Cooling or Heat Pump System was detected in the model",
            info_false: "Define a Cooling or Heat Pump System under the Systems tab",
        },
        systemServesZone: {
            label: "Does the central air handling system serve the cooling zone?",
        },
    };

    return texts[fieldName]?.[textType] || "";
};

export const getFieldLabel = (fieldName) => {
    return getFieldText(fieldName, "label");
};

export const getFieldInfo = (fieldName, condition = null) => {
    return isNull(condition) ? getFieldText(fieldName, "info") : getFieldText(fieldName, `info_${condition}`);
};

export const getFieldLockNotice = (fieldName) => {
    return getFieldText(fieldName, "lockNotice");
};

export const detectSingleZoneCoolingChanges = (currentGlobalInfo, latestAnalysisRunInfo) => {
    if (!latestAnalysisRunInfo) return {};

    const {
        outdoorDesignTemp: currentOutdoorDesignTemp,
        occupants: currentOccupants,
        homeHasCentralAirSystem: currentHomeHasCentralAirSystem,
    } = currentGlobalInfo;

    const {
        outdoorDesignTemp: latestOutdoorDesignTemp,
        occupants: latestOccupants,
        homeHasCentralAirSystem: latestHomeHasCentralAirSystem,
    } = latestAnalysisRunInfo;

    return {
        outdoorDesignTemp: latestOutdoorDesignTemp !== currentOutdoorDesignTemp,
        occupants: latestOccupants !== currentOccupants,
        homeHasCentralAirSystem: latestHomeHasCentralAirSystem !== currentHomeHasCentralAirSystem,
    };
};

export const detectZoneChanges = (currentZone, resultsZone) => {
    const {
        selectedStorey: currentSelectedStorey = "",
        selectedRooms: currentSelectedRooms = [],
        systemServesZone: currentSystemServesZone = false,
    } = currentZone;

    const {
        selectedStorey: resultsSelectedStorey = "",
        selectedRooms: resultsSelectedRooms = [],
        systemServesZone: resultsSystemServesZone = false,
    } = resultsZone;

    return (
        !isEqual(currentSelectedStorey, resultsSelectedStorey) ||
        !areArraysEqual(currentSelectedRooms, resultsSelectedRooms) ||
        !isEqual(currentSystemServesZone, resultsSystemServesZone)
    );
};

export const getFlaggedZones = (coolingZones) => {
    return Object.entries(coolingZones).reduce(
        (acc, [zoneId, zone]) => {
            const warningMessages = getZoneWarnings(zone);
            const errorMessages = getZoneErrors(zone);

            return {
                ...acc,
                zonesWarnings: {
                    ...acc.zonesWarnings,
                    [zoneId]: warningMessages,
                },
                zonesErrors: {
                    ...acc.zonesErrors,
                    [zoneId]: errorMessages,
                },
            };
        },
        { zonesWarnings: {}, zonesErrors: {} }
    );
};
